/**
 * CORP//LLM — Lobby Waiting Room
 * Full-featured waiting screen shown while a session is in 'lobby' status.
 * Two-column layout: session info + player list + ready button | embedded chat.
 */
import { t } from '../i18n';
import { getSessionDetail, markReady, leaveSession, startSession } from '../api';
import type { SessionDetail } from '../api';
import { createChatPanel } from '../chat';
import { getJwt, parseJwt } from '../auth';
import type { GameSocket } from '../ws';

const SECTORS_PER_PLAYER: Record<string, number> = { quick: 5, standard: 7, epic: 9 };

interface LobbyPlayer {
  corpName: string;
  accentColor: string;
  ready: boolean;
}

/**
 * Renders the lobby waiting room. Mounts WS event listeners via CustomEvent
 * dispatched from main.ts (lobby-player-joined, lobby-player-left, lobby-player-ready).
 */
export function renderLobbyWaiting(
  _socket: GameSocket,
  sessionId: string,
  deadlineHour: number,
): HTMLElement {
  const players: LobbyPlayer[] = [];
  let sessionName = '';
  let sessionMode = 'standard';
  let sessionSeed = 0;
  let sessionMaxPlayers = 8;
  let isReady = false;

  const root = document.createElement('div');
  root.className = 'lw-root';

  // ── Styles ──────────────────────────────────────────────────────────────
  root.innerHTML = `
    <style>
      .lw-root {
        display:flex; flex-direction:column; height:100%;
        font-family:var(--mono); color:var(--text-mid); background:var(--bg);
      }
      .lw-header {
        display:flex; align-items:center; gap:16px;
        padding:14px 24px; border-bottom:1px solid var(--border);
      }
      .lw-logo { font-size:1.1rem; letter-spacing:0.2em; color:var(--cyan); font-weight:700; }
      .lw-session-name { font-size:0.85rem; color:var(--text-lo); }
      .lw-back {
        margin-left:auto; background:none; border:1px solid var(--border);
        color:var(--text-lo); padding:4px 12px; cursor:pointer;
        font-family:var(--mono); font-size:11px; letter-spacing:0.1em;
      }
      .lw-back:hover { border-color:var(--cyan); color:var(--cyan); }
      .lw-leave {
        background:none; border:1px solid var(--red, #e63946);
        color:var(--red, #e63946); padding:4px 12px; cursor:pointer;
        font-family:var(--mono); font-size:13px;
      }
      .lw-leave:hover { background:rgba(230,57,70,0.1); }
      .lw-body { display:flex; flex:1; overflow:hidden; }
      .lw-main { flex:1; display:flex; flex-direction:column; overflow-y:auto; padding:20px 24px; gap:20px; }
      .lw-chat-col {
        width:min(360px, 35vw); min-width:280px;
        border-left:1px solid var(--border); display:flex; flex-direction:column;
      }
      .lw-panel {
        border:1px solid var(--border); padding:14px 18px;
      }
      .lw-panel-title {
        font-size:11px; letter-spacing:0.15em; color:var(--text-lo);
        text-transform:uppercase; margin-bottom:10px;
      }
      .lw-info-grid {
        display:grid; grid-template-columns:auto 1fr; gap:4px 16px;
        font-size:13px;
      }
      .lw-info-label { color:var(--text-lo); }
      .lw-info-value { color:var(--cyan); }
      .lw-countdown-row { display:flex; align-items:center; gap:12px; margin-top:10px; }
      .lw-countdown-label { font-size:12px; color:var(--text-lo); }
      .lw-countdown {
        font-size:1.4rem; font-family:var(--mono); color:var(--cyan);
        letter-spacing:0.1em;
      }
      .lw-countdown.urgent { color:var(--red, #e63946); }
      .lw-player-row {
        display:flex; align-items:center; gap:10px;
        padding:6px 0; border-bottom:1px solid rgba(255,255,255,0.04);
        font-size:13px;
      }
      .lw-player-color {
        width:10px; height:10px; border-radius:50%; flex-shrink:0;
      }
      .lw-player-name { flex:1; }
      .lw-ready-chip {
        font-size:11px; padding:2px 8px; border-radius:3px;
        border:1px solid rgba(255,255,255,0.1);
      }
      .lw-ready-chip.ready {
        color:var(--green, #34d399); border-color:rgba(52,211,153,0.3);
      }
      .lw-ready-chip.waiting { color:var(--text-lo); }
      .lw-ready-section {
        display:flex; flex-direction:column; align-items:center; gap:8px;
        padding:16px 0;
      }
      .lw-ready-btn {
        padding:12px 36px; border:2px solid var(--cyan);
        background:rgba(var(--ac),0.06); color:var(--cyan);
        font-family:var(--mono); font-size:14px; letter-spacing:0.15em;
        text-transform:uppercase; cursor:pointer; transition:all 0.15s;
      }
      .lw-ready-btn:hover { background:rgba(var(--ac),0.12); }
      .lw-ready-btn.confirmed {
        border-color:var(--green, #34d399); color:var(--green, #34d399);
        background:rgba(52,211,153,0.08); cursor:default;
      }
      .lw-ready-hint { font-size:11px; color:var(--text-lo); }
      .lw-ready-count { font-size:12px; color:var(--text-lo); margin-top:4px; }
      .lw-start-btn {
        padding:12px 36px; border:2px solid var(--amber, #fca311);
        background:rgba(252,163,17,0.08); color:var(--amber, #fca311);
        font-family:var(--mono); font-size:14px; letter-spacing:0.15em;
        text-transform:uppercase; cursor:pointer; transition:all 0.15s;
      }
      .lw-start-btn:hover { background:rgba(252,163,17,0.15); }
      .lw-start-btn:disabled { opacity:0.4; cursor:default; }
      .lw-start-hint { font-size:11px; color:var(--text-lo); }
    </style>
  `;

  // ── Header ──────────────────────────────────────────────────────────────
  const header = document.createElement('div');
  header.className = 'lw-header';
  header.innerHTML = `
    <span class="lw-logo">CORP//LLM</span>
    <span class="lw-session-name" id="lw-session-name"></span>
    <button class="lw-leave" id="lw-leave" title="Session verlassen">✕</button>
    <button class="lw-back" id="lw-back">${t('waiting.back_to_hub')}</button>
  `;
  root.appendChild(header);

  function goToHub(): void {
    sessionStorage.removeItem('corpllm_active_session');
    sessionStorage.removeItem('corpllm_active_corp');
    location.hash = '';
    location.reload();
  }

  // Back button navigates to hub
  header.querySelector('#lw-back')!.addEventListener('click', goToHub);

  // Leave button: leave the session via API, then go to hub
  header.querySelector('#lw-leave')!.addEventListener('click', async () => {
    const btn = header.querySelector('#lw-leave') as HTMLButtonElement;
    btn.disabled = true;
    try {
      await leaveSession(sessionId);
    } catch {
      // If leave fails (e.g. already left), still navigate away
    }
    goToHub();
  });

  // ── Body ────────────────────────────────────────────────────────────────
  const body = document.createElement('div');
  body.className = 'lw-body';
  root.appendChild(body);

  // Main column
  const main = document.createElement('div');
  main.className = 'lw-main';
  body.appendChild(main);

  // Info panel
  const infoPanel = document.createElement('div');
  infoPanel.className = 'lw-panel';
  infoPanel.innerHTML = `
    <div class="lw-panel-title">${t('waiting.session_info')}</div>
    <div class="lw-info-grid" id="lw-info-grid"></div>
    <div class="lw-countdown-row">
      <span class="lw-countdown-label">${t('waiting.countdown')}</span>
      <span class="lw-countdown" id="lw-countdown">--:--:--</span>
    </div>
  `;
  main.appendChild(infoPanel);

  // Players panel
  const playersPanel = document.createElement('div');
  playersPanel.className = 'lw-panel';
  playersPanel.innerHTML = `
    <div class="lw-panel-title">${t('waiting.players')}</div>
    <div id="lw-player-list"></div>
    <div class="lw-ready-count" id="lw-ready-count"></div>
  `;
  main.appendChild(playersPanel);

  // Ready section
  const readySection = document.createElement('div');
  readySection.className = 'lw-ready-section';
  readySection.innerHTML = `
    <button class="lw-ready-btn" id="lw-ready-btn">${t('waiting.ready_btn')}</button>
    <span class="lw-ready-hint">${t('waiting.ready_hint')}</span>
  `;
  main.appendChild(readySection);

  const readyBtn = readySection.querySelector('#lw-ready-btn') as HTMLButtonElement;
  readyBtn.addEventListener('click', async () => {
    if (isReady) return;
    readyBtn.disabled = true;
    try {
      await markReady(sessionId);
      isReady = true;
      readyBtn.textContent = t('waiting.ready_confirmed');
      readyBtn.classList.add('confirmed');
    } catch {
      readyBtn.disabled = false;
    }
  });

  // Start button (creator only, shown after data fetch)
  const startSection = document.createElement('div');
  startSection.className = 'lw-ready-section';
  startSection.style.display = 'none';
  startSection.innerHTML = `
    <button class="lw-start-btn" id="lw-start-btn">${t('waiting.start_btn')}</button>
    <span class="lw-start-hint">${t('waiting.start_hint')}</span>
  `;
  main.appendChild(startSection);

  const startBtn = startSection.querySelector('#lw-start-btn') as HTMLButtonElement;
  startBtn.addEventListener('click', async () => {
    startBtn.disabled = true;
    try {
      await startSession(sessionId);
    } catch {
      startBtn.disabled = false;
    }
  });

  // Chat column (embedded)
  const chatCol = document.createElement('div');
  chatCol.className = 'lw-chat-col';
  const chatPanel = createChatPanel({ embedded: true });
  chatCol.appendChild(chatPanel);
  body.appendChild(chatCol);

  // ── Render helpers ──────────────────────────────────────────────────────

  function renderInfoGrid(): void {
    const hh = String(deadlineHour).padStart(2, '0');
    const localD = new Date();
    localD.setUTCHours(deadlineHour, 0, 0, 0);
    const localStr = localD.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const eff = Math.max(sessionMaxPlayers, 4);
    const sectors = eff * (SECTORS_PER_PLAYER[sessionMode] ?? 7);

    const grid = root.querySelector('#lw-info-grid') as HTMLElement;
    grid.innerHTML = `
      <span class="lw-info-label">${t('waiting.mode_label')}</span>
      <span class="lw-info-value">${sessionMode.toUpperCase()}</span>
      <span class="lw-info-label">Seed</span>
      <span class="lw-info-value">${sessionSeed}</span>
      <span class="lw-info-label">${t('waiting.players_label')}</span>
      <span class="lw-info-value">${players.length} / ${sessionMaxPlayers}</span>
      <span class="lw-info-label">${t('waiting.city_size')}</span>
      <span class="lw-info-value">${sectors} ${t('lobby.city_size_sectors')}</span>
      <span class="lw-info-label">${t('waiting.deadline_utc')}</span>
      <span class="lw-info-value">${hh}:00 UTC (${localStr})</span>
    `;
  }

  function renderPlayerList(): void {
    const list = root.querySelector('#lw-player-list') as HTMLElement;
    list.innerHTML = '';
    for (const p of players) {
      const row = document.createElement('div');
      row.className = 'lw-player-row';
      row.setAttribute('data-corp', p.corpName);
      row.innerHTML = `
        <span class="lw-player-color" style="background:${p.accentColor}"></span>
        <span class="lw-player-name">${escHtml(p.corpName)}</span>
        <span class="lw-ready-chip ${p.ready ? 'ready' : 'waiting'}">${p.ready ? '✓' : '○'}</span>
      `;
      list.appendChild(row);
    }
    updateReadyCount();
  }

  function updateReadyCount(): void {
    const readyCount = players.filter(p => p.ready).length;
    const countEl = root.querySelector('#lw-ready-count') as HTMLElement;
    if (countEl) countEl.textContent = `${readyCount} / ${players.length} ${t('waiting.ready_btn').toLowerCase()}`;
  }

  // ── Countdown timer ─────────────────────────────────────────────────────

  const countdownEl = root.querySelector('#lw-countdown') as HTMLElement;
  const countdownTimer = setInterval(() => {
    if (!document.contains(root)) { clearInterval(countdownTimer); return; }
    const now = new Date();
    const target = new Date(now);
    target.setUTCHours(deadlineHour, 0, 0, 0);
    if (target <= now) target.setUTCDate(target.getUTCDate() + 1);
    const diff = target.getTime() - now.getTime();
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    countdownEl.textContent =
      `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    countdownEl.classList.toggle('urgent', hours < 1);
  }, 1000);

  // ── WS event handlers via CustomEvent ───────────────────────────────────

  root.addEventListener('lobby-player-joined', ((e: CustomEvent) => {
    const { corpName, accentColor } = e.detail;
    if (!players.find(p => p.corpName === corpName)) {
      players.push({ corpName, accentColor, ready: false });
      renderPlayerList();
      renderInfoGrid();
    }
  }) as EventListener);

  root.addEventListener('lobby-player-left', ((e: CustomEvent) => {
    const { corpName } = e.detail;
    const idx = players.findIndex(p => p.corpName === corpName);
    if (idx >= 0) {
      players.splice(idx, 1);
      renderPlayerList();
      renderInfoGrid();
    }
  }) as EventListener);

  root.addEventListener('lobby-player-ready', ((e: CustomEvent) => {
    const { readyCorps } = e.detail as { readyCorps?: string[] };
    if (readyCorps) {
      for (const p of players) {
        p.ready = readyCorps.includes(p.corpName);
      }
    }
    renderPlayerList();
  }) as EventListener);

  // ── Initial data fetch ──────────────────────────────────────────────────

  getSessionDetail(sessionId).then((detail: SessionDetail) => {
    sessionName = detail.name;
    sessionMode = detail.mode;
    sessionSeed = detail.seed;
    sessionMaxPlayers = detail.maxPlayers;
    deadlineHour = detail.deadlineHour;

    const nameEl = root.querySelector('#lw-session-name') as HTMLElement;
    if (nameEl) nameEl.textContent = sessionName;

    // Show start button if current user is the session creator
    const jwt = getJwt();
    const myId = jwt ? parseJwt(jwt)?.sub : null;
    if (myId && myId === detail.createdBy) {
      startSection.style.display = '';
    }

    // Populate player list from detail. Ready flag comes from the server
    // (v1.8.2+); older servers omit it and default to false, which matches
    // pre-v1.8.2 behaviour (UI-local ready state, lost on reload).
    players.length = 0;
    for (const p of detail.players) {
      players.push({
        corpName: p.corpName,
        accentColor: p.accentColor,
        ready: p.ready === true,
      });
    }

    // Restore my own ready state so the button survives a reload.
    if (myId) {
      const mine = detail.players.find(p => p.playerId === myId);
      if (mine?.ready) {
        isReady = true;
        readyBtn.disabled = true;
        readyBtn.textContent = t('waiting.ready_confirmed');
        readyBtn.classList.add('confirmed');
      }
    }

    renderInfoGrid();
    renderPlayerList();
  }).catch((err) => {
    console.warn('lobby-waiting: failed to fetch session detail:', err);
    // Render with defaults
    renderInfoGrid();
  });

  return root;
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
